import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LineageState } from "./types";
import { ColumnLineage, Confidence, CollectColumn } from "../types";
import { refsInitialState, refsReducers } from "./refsSliceFragment";

const initialState = {
  selectedTable: "",
  moreTables: {},
  sidebarScreen: "",
  selectedColumn: undefined,
  collectColumns: {},
  confidence: { confidence: "high" },
  leftExpansion: 0,
  rightExpansion: 0,
  minRange: [0, 0],
  nodeCount: 0,
  selectCheck: true,
  nonSelectCheck: true,
  defaultExpansion: 1,
  aiEnabled: false,
  modalArgs: null,
  theme: "dark",
  lineageType: "dynamic",
  highlightedNodes: [],
  externalSidePanel: false,
  selectedNode: "",
  nodeSavingsPerformance: {},
  nodesCost: {},
  errors: {},
  lightdashEnabled: false,
  showCodeModal: false,
  renderTags: undefined,
  config: { exportFinalLineage: false },
  client: "extension",
  ...refsInitialState,
} as LineageState;

const lineageSlice = createSlice({
  name: "lineageState",
  initialState,
  reducers: {
    setAllowSyncColumnsWithDB: (
      state,
      action: PayloadAction<LineageState["allowSyncColumnsWithDB"]>
    ) => {
      state.allowSyncColumnsWithDB = action.payload;
    },
    setStaticLineage: (state, action: PayloadAction<LineageState["sqlLineage"]>) => {
      state.sqlLineage = action.payload;
    },
    setLineageType: (state, action: PayloadAction<LineageState["lineageType"]>) => {
      state.lineageType = action.payload;
    },
    setTheme: (state, action: PayloadAction<LineageState["theme"]>) => {
      state.theme = action.payload;
    },
    setSelectedTable: (state, action: PayloadAction<LineageState["selectedTable"]>) => {
      state.selectedTable = action.payload;
    },
    setModalArgs: (state, action: PayloadAction<LineageState["modalArgs"]>) => {
      state.modalArgs = action.payload;
    },
    setAiEnabled: (state, action: PayloadAction<LineageState["aiEnabled"]>) => {
      state.aiEnabled = action.payload;
    },
    setMoreTables: (state, action: PayloadAction<LineageState["moreTables"]>) => {
      state.moreTables = action.payload;
    },
    mergeSeeMoreTables: (state, action: PayloadAction<ColumnLineage[]>) => {
      state.moreTables = {
        ...state.moreTables,
        lineage: [...(state.moreTables.lineage || []), ...action.payload],
      };
    },
    setSidebarScreen: (state, action: PayloadAction<LineageState["sidebarScreen"]>) => {
      state.sidebarScreen = action.payload;
    },
    setSelectedColumn: (state, action: PayloadAction<LineageState["selectedColumn"]>) => {
      state.selectedColumn = action.payload;
    },
    setCollectColumns: (state, action: PayloadAction<LineageState["collectColumns"]>) => {
      state.collectColumns = action.payload;
    },
    mergeCollectColumns: (state, action: PayloadAction<Record<string, CollectColumn[]>>) => {
      const newCollectColumns = action.payload;
      const collectColumns: Record<string, CollectColumn[]> = {
        ...state.collectColumns,
      };
      for (const t in newCollectColumns) {
        const _columns = newCollectColumns[t];
        if (!(t in collectColumns)) {
          collectColumns[t] = _columns;
          continue;
        }
        // merge _columns with collectColumns[t]
        const updatedColumns = _columns
          .map(c => {
            const existing = collectColumns[t].findIndex(x => x.column === c.column);
            if (existing === -1) return c;
            if (c.viewsType) collectColumns[t][existing].viewsType = c.viewsType;
            return null;
          })
          .filter((c): c is CollectColumn => c !== null);
        collectColumns[t].push(...updatedColumns);
      }
      state.collectColumns = collectColumns;
    },
    setConfidence: (state, action: PayloadAction<LineageState["confidence"]>) => {
      state.confidence = action.payload;
    },
    updateConfidenceWithOperatorList: (
      state,
      action: PayloadAction<{ operatorList: Confidence["operator_list"] }>
    ) => {
      const newConfidence = { ...state.confidence, confidence: "low" };
      newConfidence.operator_list = newConfidence.operator_list || [];
      newConfidence.operator_list.push(...(action.payload.operatorList || []));
      state.confidence = newConfidence;
    },
    setLeftExpansion: (state, action: PayloadAction<LineageState["leftExpansion"]>) => {
      state.leftExpansion = action.payload;
    },
    setRightExpansion: (state, action: PayloadAction<LineageState["rightExpansion"]>) => {
      state.rightExpansion = action.payload;
    },
    setMinRange: (state, action: PayloadAction<LineageState["minRange"]>) => {
      state.minRange = action.payload;
    },
    setNodeCount: (state, action: PayloadAction<LineageState["nodeCount"]>) => {
      state.nodeCount = action.payload;
    },
    setSelectCheck: (state, action: PayloadAction<LineageState["selectCheck"]>) => {
      state.selectCheck = action.payload;
    },
    setNonSelectCheck: (state, action: PayloadAction<LineageState["nonSelectCheck"]>) => {
      state.nonSelectCheck = action.payload;
    },
    setDefaultExpansion: (state, action: PayloadAction<LineageState["defaultExpansion"]>) => {
      state.defaultExpansion = action.payload;
    },
    setSqlLineageDetails: (state, action: PayloadAction<LineageState["sqlLineage"]>) => {
      state.sqlLineage = action.payload;
    },
    setHighlightedNodes: (state, action: PayloadAction<LineageState["highlightedNodes"]>) => {
      state.highlightedNodes = action.payload;
    },
    setSelectedNode: (state, action: PayloadAction<LineageState["selectedNode"]>) => {
      state.selectedNode = action.payload;
    },
    setNodesSavingsPerformance: (
      state,
      action: PayloadAction<LineageState["nodeSavingsPerformance"]>
    ) => {
      state.nodeSavingsPerformance = action.payload;
    },
    setNodesCost: (state, action: PayloadAction<LineageState["nodesCost"]>) => {
      state.nodesCost = action.payload;
    },
    setErrors: (state, action: PayloadAction<LineageState["errors"]>) => {
      state.errors = action.payload;
    },
    setLightdashEnabled: (state, action: PayloadAction<LineageState["lightdashEnabled"]>) => {
      state.lightdashEnabled = action.payload;
    },
    setShowCodeModal: (state, action: PayloadAction<LineageState["showCodeModal"]>) => {
      state.showCodeModal = action.payload;
    },
    setRenderTags: (state, action: PayloadAction<LineageState["renderTags"]>) => {
      state.renderTags = action.payload;
    },
    setConfig: (state, action: PayloadAction<LineageState["config"]>) => {
      state.config = action.payload;
    },
    setClient: (state, action: PayloadAction<LineageState["client"]>) => {
      state.client = action.payload;
    },
    ...refsReducers,
  },
});

export const {
  setSelectedTable,
  setMoreTables,
  mergeSeeMoreTables,
  setSidebarScreen,
  setSelectedColumn,
  setCollectColumns,
  mergeCollectColumns,
  setConfidence,
  updateConfidenceWithOperatorList,
  setLeftExpansion,
  setRightExpansion,
  setMinRange,
  setNodeCount,
  setSelectCheck,
  setNonSelectCheck,
  setDefaultExpansion,
  setAiEnabled,
  setModalArgs,
  setTheme,
  setLineageType,
  setStaticLineage,
  setAllowSyncColumnsWithDB,
  setSqlLineageDetails,
  setHighlightedNodes,
  setSelectedNode,
  setNodesSavingsPerformance,
  setNodesCost,
  setErrors,
  setLightdashEnabled,
  setShowCodeModal,
  setRenderTags,
  setConfig,
  setClient,
  setRefs,
  setShowRefs,
  setEnabledRefSources,
  setRefSourceEnabled,
  setInferenceConfidenceThreshold,
  setIncludeSourcesInInference,
  setAllTablesExpanded,
} = lineageSlice.actions;
export default lineageSlice;
